import { BehaviorSubject } from 'rxjs'
import { EditorAssistance } from '../../../models/EditorAssistance'
import { isBefore } from 'date-fns'
import { AssistanceStatusesService } from '../../../services/domain/codelists/assistance-statuses.service'
import { shareReplay } from 'rxjs/operators'

export class AssistanceEditorModalEditableHandler {
  private editable$$ = new BehaviorSubject<boolean>(false)
  private forcedEditable: boolean = null

  constructor(private statusService: AssistanceStatusesService) {}

  /**
   * Forces the editable state of the instance (forever).
   *
   * @param {boolean} state - The desired editable state to be applied.
   * @return {void}
   */
  forceEditable(state: boolean): void {
    this.forcedEditable = state
    this.setEditable(state)
  }

  getEditable() {
    return this.editable$$.asObservable().pipe(shareReplay(1))
  }

  private setEditable(state: boolean) {
    this.editable$$.next(state)
  }

  /**
   * Determines and sets the editability of a given assistance object based on various conditions.
   *
   * @param {EditorAssistance} assistance - The assistance object to be evaluated for editability.
   * @return {Promise<void>} A promise that resolves when the editability status has been determined and set.
   */
  async calculateEditable(assistance: EditorAssistance): Promise<void> {
    if (this.forcedEditable !== null) {
      return this.setEditable(this.forcedEditable)
    }

    if (assistance.isNotSavedInDatabase()) {
      return this.setEditable(true)
    }

    if (isBefore(assistance.getRealizedTo(), new Date())) {
      return this.setEditable(false)
    }

    if ((await this.statusService.getByIdOnce(assistance.statusId)).isNegative()) {
      return this.setEditable(false)
    }

    return this.setEditable(true)
  }
}
